import Button from 'ext-ui/components/Button/Button'
import Pill from 'ext-ui/components/Pill/Pill'
import Processing from 'ext-ui/components/Processing/Processing'
import Switch from 'ext-ui/components/Switch/Switch'
import ExtUITextInput from 'ext-ui/components/TextInput/TextInput'
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import type { MerchantCategoryRestrictionsState } from 'src/feature/UpdatedPhysicalCardSettings/UpdatedPhysicalCardSettings'
import { useTranslate } from 'src/localization/Trns'
import type { CardManagementGeofenceCountry } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

import SettingsControlRow from '../SettingsControlRow/SettingsControlRow'
import SettingsDisclosure from '../SettingsDisclosure/SettingsDisclosure'
import SettingsDrawerShell from '../SettingsDrawerShell/SettingsDrawerShell'
import SettingsTile from '../SettingsTile/SettingsTile'
import MerchantCategoryRestrictions from './MerchantCategoryRestrictions'
import style from './SpendRestrictionsTile.style'

const PILL_CAP = 4

/** Stable identity, so the drawer's reset effect does not re-fire every render. */
const NOOP = (): void => undefined

type ChannelKey = 'online' | 'swipe' | 'atm' | 'wallet'

function cappedPills(pills: ReactNode[]): ReactNode[] {
  if (pills.length <= PILL_CAP) {
    return pills
  }
  return [
    ...pills.slice(0, PILL_CAP),
    <Pill key="more" size="small">{`+${pills.length - PILL_CAP} more`}</Pill>,
  ]
}

/**
 * Geofence as the tile needs it.
 *
 * `permittedCountries` is nullable for the same reason a payment channel's
 * `enabled` is: an UNAVAILABLE snapshot has no list, and rendering `[]` would
 * assert "blocked worldwide" — a real, different claim the contract explicitly
 * forbids guessing.
 */
export type GeofenceView = {
  permittedCountries: CardManagementGeofenceCountry[] | null
  availability: 'AVAILABLE' | 'READ_ONLY' | 'UNAVAILABLE'
  reasonCode: string | null
  /** Fence for the next change. Null when UNAVAILABLE. */
  version: number | null
}

export type GeofenceState = 'loading' | 'ready' | 'unavailable' | 'error'

export type GeofenceCountrySearchState = 'loading' | 'ready' | 'error'

/** Why the geofence is not editable, in the user's terms. */
function geofenceUnavailableText(
  translate: (key: string) => string,
  view: { availability: string; reasonCode: string | null },
): string {
  if (view.reasonCode === 'ROLE_NOT_ALLOWED') {
    return translate('Your role cannot change where this card can be used.')
  }
  if (view.reasonCode === 'CARD_STATE_NOT_ALLOWED') {
    return translate("This card's current status does not allow that action.")
  }
  if (view.availability === 'READ_ONLY') {
    return translate(
      'Where this card can be used is managed elsewhere and cannot be changed here.',
    )
  }
  return translate('Geofence controls are not available on this card.')
}

function GeofenceDrawer(props: {
  open: boolean
  onClose: () => void
  permittedCountries: CardManagementGeofenceCountry[]
  /** False renders the drawer as a read-only list of permitted countries. */
  canEdit: boolean
  query: string
  onQueryChange: (query: string) => void
  results: CardManagementGeofenceCountry[]
  searchState: GeofenceCountrySearchState
  onAdd: (countryCode: string) => void
  onRemove: (countryCode: string) => void
  /** The single in-flight change, if any. */
  pending?: { change: 'ADD' | 'REMOVE'; countryCode: string }
  saveError?: string
}): ReactElement {
  const {
    open,
    onClose,
    permittedCountries,
    canEdit,
    query,
    onQueryChange,
    results,
    searchState,
    onAdd,
    onRemove,
    pending,
    saveError,
  } = props
  const translate = useTranslate()

  useEffect(() => {
    if (!open) {
      onQueryChange('')
    }
  }, [open, onQueryChange])

  const permittedCodes = new Set(
    permittedCountries.map((country) => country.countryCode),
  )
  // Already-permitted countries are filtered out: they are listed below under
  // "Permitted", and adding one is a documented no-op.
  const addable = results.filter(
    (country) => !permittedCodes.has(country.countryCode),
  )

  // ONE version fences the whole resource — unlike payment channels, where each
  // channel carries its own. A second click while a change is in flight would
  // send the version the first click already consumed and fail with
  // CARD-MGMT.22, so every control locks, not just the row in flight.
  const locked = !canEdit || pending !== undefined

  return (
    <SettingsDrawerShell
      isOpen={open}
      onClose={onClose}
      title={translate('Geofence')}
      lead={
        canEdit
          ? translate(
              'Choose where this card can be used. Changes apply immediately.',
            )
          : translate('Where this card can be used. Read-only.')
      }
      /*
        The design closes the drawer with an info note restating the effect.
        Worded per the contract, not the prototype: Slack feedback replaced
        "approvals ... regions" with the customer-facing "card transactions
        ... countries", and the contract adopts that language.
      */
      footnote={translate(
        'Card transactions outside the permitted countries are declined. Applies instantly.',
      )}
    >
      <div className="SpendRestrictionsTile-drawer" css={style}>
        {canEdit && (
          <div className="SpendRestrictionsTile-drawerSection">
            <ExtUITextInput
              variant="small"
              label={translate('Add a country')}
              placeholder={translate('Search countries…')}
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              testId="geofence-country-search"
            />
            {/*
              Results appear only once the user has actually typed. Rendering
              the list on open — with the search state defaulting to `loading` —
              put a spinner under an empty search box, which reads as a search
              already in progress, and fired a catalogue request nobody asked
              for. Matches the design's own `query.trim() !== ''` gate.
            */}
            {query.trim() !== '' && (
              <ul className="SpendRestrictionsTile-addList">
                {searchState === 'error' ? (
                  <li className="SpendRestrictionsTile-addEmpty" role="alert">
                    {translate(
                      "We couldn't load the country list. Try again in a moment.",
                    )}
                  </li>
                ) : searchState === 'loading' ? (
                  <li className="SpendRestrictionsTile-addEmpty">
                    {translate('Loading')}
                  </li>
                ) : addable.length === 0 ? (
                  <li className="SpendRestrictionsTile-addEmpty">
                    {translate('No matches')}
                  </li>
                ) : (
                  addable.map((country) => (
                    <li key={country.countryCode}>
                      <button
                        type="button"
                        className="SpendRestrictionsTile-addRow"
                        disabled={locked}
                        onClick={() => onAdd(country.countryCode)}
                        data-testid={`geofence-add-${country.countryCode}`}
                      >
                        <span>{country.displayName}</span>
                        <span aria-hidden="true">
                          {pending?.change === 'ADD' &&
                          pending.countryCode === country.countryCode
                            ? translate('Saving…')
                            : translate('+ add')}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}

        <div className="SpendRestrictionsTile-drawerSection">
          <div className="SpendRestrictionsTile-drawerKicker">
            {permittedCountries.length > 0
              ? `${translate('Permitted')} · ${permittedCountries.length}`
              : translate('Permitted')}
          </div>
          {permittedCountries.length === 0 ? (
            <p className="SpendRestrictionsTile-drawerEmpty">
              {translate('No countries — the card is blocked worldwide.')}
            </p>
          ) : (
            <ul className="SpendRestrictionsTile-exceptionList isGeofence">
              {permittedCountries.map((country) => (
                <li
                  key={country.countryCode}
                  className="SpendRestrictionsTile-exceptionRow"
                >
                  <span className="SpendRestrictionsTile-exceptionName">
                    {country.displayName}
                  </span>
                  {canEdit && (
                    <button
                      type="button"
                      className="SpendRestrictionsTile-removeButton"
                      disabled={locked}
                      aria-label={translate('Remove ${name}', {
                        name: country.displayName,
                      })}
                      onClick={() => onRemove(country.countryCode)}
                      data-testid={`geofence-remove-${country.countryCode}`}
                    >
                      {'×'}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {saveError !== undefined && (
          <div
            className="SpendRestrictionsTile-drawerFooter"
            role="alert"
            data-testid="geofence-save-error"
          >
            {saveError}
          </div>
        )}
      </div>
    </SettingsDrawerShell>
  )
}

/** Contract vocabulary. The tile's `ChannelKey` is the UI spelling. */
export type PaymentChannelId =
  | 'ONLINE_ECOMMERCE'
  | 'IN_STORE'
  | 'ATM'
  | 'MOBILE_WALLET'

/**
 * Why a channel is not operable, in the user's terms.
 *
 * The contract's reason vocabulary is shared across Card Management, but only a
 * few values are actionable to a cardholder; the rest collapse to one honest
 * "not available on this card" rather than leaking processor vocabulary into
 * the UI. Never renders the raw code.
 */
function channelUnavailableText(
  translate: (key: string) => string,
  view: { availability: string; reasonCode: string | null },
): string {
  if (view.reasonCode === 'ROLE_NOT_ALLOWED') {
    return translate('Your role cannot change this channel.')
  }
  if (view.reasonCode === 'CARD_STATE_NOT_ALLOWED') {
    return translate("This card's current status does not allow that action.")
  }
  if (view.availability === 'READ_ONLY') {
    return translate(
      'This channel is managed elsewhere and cannot be changed here.',
    )
  }
  return translate('This channel is not available on this card.')
}

const CHANNEL_ID_BY_KEY: Record<ChannelKey, PaymentChannelId> = {
  online: 'ONLINE_ECOMMERCE',
  swipe: 'IN_STORE',
  atm: 'ATM',
  wallet: 'MOBILE_WALLET',
}

/**
 * One channel as the tile needs it. `enabled` is nullable because an
 * UNAVAILABLE channel genuinely has no on/off value — the contract forbids
 * guessing one, so the row shows its reason instead of a switch position.
 */
export type PaymentChannelView = {
  channel: PaymentChannelId
  enabled: boolean | null
  availability: 'AVAILABLE' | 'READ_ONLY' | 'UNAVAILABLE'
  reasonCode: string | null
  version: number | null
}

export type PaymentChannelsState = 'loading' | 'ready' | 'unavailable' | 'error'

type SpendRestrictionsTileProps = {
  /** Grid-column class from the bento parent. */
  className?: string
  merchantCategoryRestrictions: MerchantCategoryRestrictionsState
  /**
   * `true` reads and writes the Card Management payment-channels resource
   * instead of the placeholder local state.
   */
  usePaymentChannelsApi?: boolean
  /**
   * EX-40629 — the Card Management geofence resource. No client flag: like the
   * MCC group, the geofence is state-driven end to end, and the snapshot's own
   * `availability` (plus `geofenceState`) is the only gate. The page owns the
   * reads, the country search, and the write.
   */
  geofence?: GeofenceView
  geofenceState?: GeofenceState
  /**
   * Raw search text, lifted so the page owns debouncing and the query key.
   *
   * The options come from the contract's CARD-SCOPED
   * `/geofence/countries` search, not the app's own `/countries` catalogue:
   * only the server knows which countries are a valid selection for this card,
   * and it authors the display names. A locally-sourced list would offer codes
   * the change then rejects with `CARD-MGMT.23`.
   */
  geofenceCountryQuery?: string
  onGeofenceCountryQueryChange?: (query: string) => void
  geofenceCountryResults?: CardManagementGeofenceCountry[]
  geofenceCountrySearchState?: GeofenceCountrySearchState
  /**
   * Adds or removes ONE country. The contract has no multi-country mutation,
   * and the whole resource shares a single version, so the tile hands over the
   * change and the snapshot's version rather than a whole-collection payload.
   */
  onChangeGeofenceCountry?: (
    change: 'ADD' | 'REMOVE',
    countryCode: string,
    expectedVersion: number,
  ) => void
  pendingGeofenceCountry?: { change: 'ADD' | 'REMOVE'; countryCode: string }
  geofenceSaveError?: string
  paymentChannels?: PaymentChannelView[]
  paymentChannelsState?: PaymentChannelsState
  /**
   * Sets ONE channel. The contract has no multi-channel mutation, and each
   * channel fences on its own version, so the tile hands over the channel and
   * its version rather than a whole-collection payload.
   */
  onSetPaymentChannel?: (
    channel: PaymentChannelId,
    enabled: boolean,
    expectedVersion: number,
  ) => void
  /** The channel currently in flight, if any. */
  /**
   * The in-flight write, with the value the user asked for. The desired value
   * is needed, not just the id: without it the switch keeps rendering the old
   * server state for the whole round trip, so it visibly springs back and
   * greys out — the standard vocabulary for "rejected" — and a successful save
   * looks like a failure until the PUT resolves.
   */
  pendingPaymentChannel?: { channel: PaymentChannelId; enabled: boolean }
  paymentChannelsSaveError?: string
  /**
   * Which channel the failed write targeted. The error belongs on that row —
   * a message at the foot of the list makes the reader hunt for which switch
   * it refers to, and reads as if the whole section failed.
   */
  failedPaymentChannel?: PaymentChannelId
  /**
   * The channel whose write just succeeded. EX-40627 asks for a success state
   * alongside loading/disabled/pending/failure; without it a toggle that
   * lands looks identical to one that silently did nothing.
   */
  savedPaymentChannel?: PaymentChannelId
}

/**
 * "Where & how it can spend" tile. Merchant categories, payment channels and
 * the geofence are all persisted to the card once their APIs are connected.
 */
export default function SpendRestrictionsTile(
  props: SpendRestrictionsTileProps,
): ReactElement {
  const {
    className,
    merchantCategoryRestrictions,
    usePaymentChannelsApi = false,
    paymentChannels,
    paymentChannelsState = 'loading',
    onSetPaymentChannel,
    pendingPaymentChannel,
    paymentChannelsSaveError,
    failedPaymentChannel,
    savedPaymentChannel,
    geofence,
    geofenceState = 'loading',
    geofenceCountryQuery = '',
    onGeofenceCountryQueryChange,
    geofenceCountryResults,
    geofenceCountrySearchState = 'loading',
    onChangeGeofenceCountry,
    pendingGeofenceCountry,
    geofenceSaveError,
  } = props
  const translate = useTranslate()
  const [channels, setChannels] = useState<Record<ChannelKey, boolean>>({
    online: true,
    swipe: true,
    atm: false,
    wallet: true,
  })
  const [geofenceDrawerOpen, setGeofenceDrawerOpen] = useState(false)
  const [paymentChannelsDrawerOpen, setPaymentChannelsDrawerOpen] =
    useState(false)

  // Null is NOT an empty list. An UNAVAILABLE snapshot has no list at all, and
  // `[]` is the card's real "blocked worldwide" state — collapsing the two
  // would tell the user the card is blocked everywhere when the server simply
  // declined to say.
  const permittedCountries = geofence?.permittedCountries ?? null
  const geofenceIsEditable =
    geofenceState === 'ready' &&
    geofence?.availability === 'AVAILABLE' &&
    geofence.version !== null &&
    onChangeGeofenceCountry !== undefined

  const channelByIdView = new Map(
    (paymentChannels ?? []).map((channel) => [channel.channel, channel]),
  )
  /** Server state on the connected path, local placeholder otherwise. */
  const channelView = (key: ChannelKey): PaymentChannelView | undefined =>
    usePaymentChannelsApi
      ? channelByIdView.get(CHANNEL_ID_BY_KEY[key])
      : undefined
  const channelIsOn = (key: ChannelKey): boolean => {
    if (!usePaymentChannelsApi) return channels[key]
    // Hold the requested value while its own write is in flight. The row
    // renders a spinner rather than a switch meanwhile, so this is not what
    // the user sees there — but the collapsed summary pills read through here
    // too, and they must not contradict the change already under way.
    if (pendingPaymentChannel?.channel === CHANNEL_ID_BY_KEY[key]) {
      return pendingPaymentChannel.enabled
    }
    // Unknown is NOT off. An UNAVAILABLE channel has no value, and rendering
    // the switch off would assert the channel is disabled when the server
    // explicitly declined to say.
    return channelView(key)?.enabled === true
  }

  const channelRows: {
    key: ChannelKey
    label: string
    shortLabel: string
    description: string
  }[] = [
    {
      key: 'online',
      label: translate('Online & e-commerce'),
      shortLabel: translate('Online'),
      description: translate('Card-not-present purchases'),
    },
    {
      key: 'swipe',
      label: translate('In-store (swipe / tap)'),
      shortLabel: translate('In-store'),
      description: translate('Card-present terminals'),
    },
    {
      key: 'atm',
      label: translate('ATM withdrawals'),
      shortLabel: translate('ATM'),
      description: translate('Cash access'),
    },
    {
      key: 'wallet',
      label: translate('Mobile wallet'),
      shortLabel: translate('Mobile wallet'),
      description: translate('Apple Pay / Google Pay'),
    },
  ]
  /*
   * Which channel's switch to hand focus back to when it remounts.
   *
   * A row's switch is unmounted for the length of its write (the spinner takes
   * its slot), and unmounting the element a keyboard user just activated drops
   * them at the top of the document — WCAG 2.4.3.
   *
   * Set when the user flips a switch, and disarmed in three places so it can
   * never fire against a control the user did not just leave: consumed on the
   * ref callback's first run, cleared when the drawer closes, and gated on
   * focus actually sitting at <body> before anything is refocused.
   */
  const refocusChannelRef = useRef<ChannelKey | null>(null)

  const channelLabelFor = (channel: PaymentChannelId): string | undefined =>
    channelRows.find((row) => CHANNEL_ID_BY_KEY[row.key] === channel)?.label

  /*
   * What the payment-channels drawer's live region says.
   *
   * The switches report an in-flight write visually by swapping to a spinner,
   * which says nothing to a screen reader; this is that state in words. It
   * replaces the per-row "Saving…"/"Saved" text, which announced the same thing
   * but did it by growing and shrinking the row (design QA).
   *
   * Rendered into ONE region that is mounted for the drawer's whole life, with
   * only its text changing — a region inserted with its text already in place
   * is frequently never announced at all.
   */
  const paymentChannelAnnouncement = ((): string => {
    if (!usePaymentChannelsApi) return ''
    // Pending wins: a fresh write on one channel while another has just landed
    // should report the write, not the stale confirmation.
    const pendingLabel =
      pendingPaymentChannel !== undefined
        ? channelLabelFor(pendingPaymentChannel.channel)
        : undefined
    if (pendingLabel !== undefined) {
      return translate('Saving ${channel}', { channel: pendingLabel })
    }
    const savedLabel =
      savedPaymentChannel !== undefined
        ? channelLabelFor(savedPaymentChannel)
        : undefined
    if (savedLabel !== undefined) {
      return translate('${channel} saved', { channel: savedLabel })
    }
    return ''
  })()

  const channelSummary = (
    <span className="SpendRestrictionsTile-summaryPills">
      {cappedPills(
        [...channelRows]
          // Server state on the connected path, not the local preview state —
          // the collapsed summary must not contradict the switches below it.
          .sort(
            (a, b) => Number(channelIsOn(b.key)) - Number(channelIsOn(a.key)),
          )
          .map((row) => {
            const view = channelView(row.key)
            // An UNAVAILABLE channel has NO on/off value. A "severe" pill would
            // read as "off", which the contract explicitly forbids guessing —
            // so it gets the neutral pending treatment instead.
            const unknown =
              usePaymentChannelsApi && view?.availability === 'UNAVAILABLE'
            return (
              <Pill
                key={row.key}
                size="small"
                color={
                  unknown ? 'pending' : channelIsOn(row.key) ? 'saas' : 'severe'
                }
              >
                {row.shortLabel}
              </Pill>
            )
          }),
      )}
    </span>
  )
  const geofenceSummary =
    permittedCountries === null ? (
      <span className="SpendRestrictionsTile-summaryText">
        {geofenceState === 'error'
          ? translate('Unavailable')
          : geofenceState === 'loading'
            ? translate('Loading')
            : translate('Not available')}
      </span>
    ) : permittedCountries.length === 0 ? (
      <span className="SpendRestrictionsTile-summaryText">
        {translate('Worldwide blocked')}
      </span>
    ) : (
      <span className="SpendRestrictionsTile-summaryPills">
        {cappedPills(
          permittedCountries.map((country) => (
            <Pill key={country.countryCode} size="small">
              {country.displayName}
            </Pill>
          )),
        )}
      </span>
    )

  // The version comes from the snapshot on every call, never from a captured
  // closure: each successful change returns a NEW version, and the drawer stays
  // open across several of them.
  const changeGeofenceCountry = (
    change: 'ADD' | 'REMOVE',
    countryCode: string,
  ): void => {
    const expectedVersion = geofence?.version
    if (expectedVersion == null) return
    onChangeGeofenceCountry?.(change, countryCode, expectedVersion)
  }

  return (
    <SettingsTile
      className={className}
      title={translate('Where & how it can spend')}
    >
      <div className="SpendRestrictionsTile" css={style}>
        <p className="SpendRestrictionsTile-intro">
          {translate(
            'Merchant categories, payment channels and geofence are saved to this card. Changes apply immediately.',
          )}
        </p>

        <MerchantCategoryRestrictions state={merchantCategoryRestrictions} />

        <SettingsDisclosure
          title={translate('Payment channels')}
          summary={channelSummary}
          actionLabel={translate('Edit')}
        >
          {usePaymentChannelsApi && paymentChannelsState === 'error' ? (
            <p
              className="SpendRestrictionsTile-body"
              role="alert"
              data-testid="payment-channels-load-error"
            >
              {translate(
                "We couldn't load this card's payment channels. Refresh to try again.",
              )}
            </p>
          ) : usePaymentChannelsApi &&
            paymentChannelsState === 'unavailable' ? (
            <p
              className="SpendRestrictionsTile-body"
              data-testid="payment-channels-view-unavailable"
            >
              {translate(
                'You do not have permission to view this card\u2019s payment channels.',
              )}
            </p>
          ) : usePaymentChannelsApi && paymentChannelsState !== 'ready' ? (
            <p
              className="SpendRestrictionsTile-body"
              data-testid="payment-channels-loading"
            >
              {translate('Loading')}
            </p>
          ) : (
            // The disclosure's own `summary` (`channelSummary`) already
            // carries every channel's state as pills — repeating the rows
            // here would duplicate it the same way the Geofence disclosure
            // used to.
            <Button
              variant="secondary"
              dropShadow={false}
              onClick={() => setPaymentChannelsDrawerOpen(true)}
              data-testid="edit-payment-channels"
            >
              {translate('Edit payment channels')}
            </Button>
          )}
        </SettingsDisclosure>

        <SettingsDrawerShell
          isOpen={paymentChannelsDrawerOpen}
          onClose={() => {
            // BaseFlyout renders through react-modal, which unmounts its
            // children on close — so a switch that never came back from its
            // write would leave the ref armed, and pull focus on reopen.
            refocusChannelRef.current = null
            setPaymentChannelsDrawerOpen(false)
          }}
          title={translate('Payment channels')}
          lead={translate(
            'Disable a channel to shrink the fraud surface. Changes apply instantly and are logged in Activity.',
          )}
          footnote={translate(
            'Turned-off channels decline in real time. Applies instantly.',
          )}
        >
          <div className="SpendRestrictionsTile-drawerContent" css={style}>
            <div
              className="SpendRestrictionsTile-visuallyHidden"
              role="status"
              data-testid="payment-channels-announcer"
            >
              {paymentChannelAnnouncement}
            </div>
            {channelRows.map((row) => {
              const view = channelView(row.key)
              // Only an AVAILABLE channel is operable. READ_ONLY renders its
              // real value but PUT would 403; UNAVAILABLE has no value at all.
              // No handler means the page withheld it because
              // MANAGE_PHYSICAL_CARD_PAYMENT_CHANNELS is denied — an
              // operable switch would promise a PUT that returns 403.
              const operable =
                !usePaymentChannelsApi ||
                (onSetPaymentChannel !== undefined &&
                  view?.availability === 'AVAILABLE' &&
                  view.version !== null &&
                  // Only THIS row. Each channel fences on its own version, so
                  // concurrent writes to different channels cannot conflict —
                  // freezing all four on one slow ATM write is broader than
                  // the contract needs.
                  pendingPaymentChannel?.channel !== CHANNEL_ID_BY_KEY[row.key])
              return (
                <SettingsControlRow
                  key={row.key}
                  title={row.label}
                  description={
                    usePaymentChannelsApi &&
                    view !== undefined &&
                    view.availability !== 'AVAILABLE'
                      ? channelUnavailableText(translate, view)
                      : row.description
                  }
                  error={
                    usePaymentChannelsApi &&
                    failedPaymentChannel === CHANNEL_ID_BY_KEY[row.key]
                      ? paymentChannelsSaveError
                      : undefined
                  }
                  errorTestId={`channel-${row.key}-error`}
                >
                  {/*
                      An UNAVAILABLE channel has NO on/off value. A disabled
                      switch still renders in the off position, which asserts
                      "this channel is disabled" — the guess the contract
                      forbids. Show a neutral status instead of a control.
                    */}
                  {usePaymentChannelsApi &&
                  view?.availability === 'UNAVAILABLE' ? (
                    <Pill size="small" color="pending">
                      {translate('Not available')}
                    </Pill>
                  ) : usePaymentChannelsApi &&
                    pendingPaymentChannel?.channel ===
                      CHANNEL_ID_BY_KEY[row.key] ? (
                    /*
                      In flight: the spinner stands in the switch's own slot,
                      at the switch's own size, so the row neither grows nor
                      jumps (design QA — the "Saving…"/"Saved" text this
                      replaces was a third line that appeared and vanished).
                      Decorative here; the drawer's live region below carries
                      the same information for assistive technology.
                    */
                    <span
                      className="SpendRestrictionsTile-switchPending"
                      data-testid={`channel-${row.key}-pending`}
                    >
                      <Processing width={16} height={16} aria-hidden="true" />
                    </span>
                  ) : (
                    <Switch
                      ref={(node) => {
                        if (
                          node === null ||
                          refocusChannelRef.current !== row.key
                        ) {
                          return
                        }
                        // Consume it on the first fire either way: an inline
                        // ref callback re-runs on every render, not only on
                        // remount, so an armed ref that is never cleared would
                        // keep firing.
                        refocusChannelRef.current = null
                        // Only repair the focus THIS swap dropped. A removed
                        // element sends focus to <body>; anywhere else means
                        // the user still holds the control or has moved on
                        // deliberately, and taking it back would be worse than
                        // the problem.
                        if (document.activeElement === document.body) {
                          node.focus()
                        }
                      }}
                      checked={channelIsOn(row.key)}
                      disabled={!operable}
                      ariaLabel={row.label}
                      onCheckedChange={(checked) => {
                        if (!usePaymentChannelsApi) {
                          setChannels((prev) => ({
                            ...prev,
                            [row.key]: checked,
                          }))
                          return
                        }
                        // Fence on THIS channel's version, not a shared one.
                        if (view?.version == null) return
                        // Only the connected path swaps this switch out for a
                        // spinner, so only it has focus to give back.
                        refocusChannelRef.current = row.key
                        onSetPaymentChannel?.(
                          CHANNEL_ID_BY_KEY[row.key],
                          checked,
                          view.version,
                        )
                      }}
                      data-testid={`channel-${row.key}`}
                    />
                  )}
                </SettingsControlRow>
              )
            })}
          </div>
        </SettingsDrawerShell>

        <SettingsDisclosure
          title={translate('Geofence')}
          summary={geofenceSummary}
          actionLabel={
            geofenceIsEditable ? translate('Edit') : translate('View')
          }
        >
          <p className="SpendRestrictionsTile-body">
            {translate(
              'Card transactions outside the permitted countries are declined. All other countries are blocked.',
            )}
          </p>
          {geofenceState === 'error' ? (
            <p
              className="SpendRestrictionsTile-body"
              role="alert"
              data-testid="geofence-load-error"
            >
              {translate(
                "We couldn't load this card's geofence. Refresh to try again.",
              )}
            </p>
          ) : geofenceState === 'loading' ? (
            <p
              className="SpendRestrictionsTile-body"
              data-testid="geofence-loading"
            >
              {translate('Loading')}
            </p>
          ) : geofenceState === 'unavailable' ? (
            <p
              className="SpendRestrictionsTile-body"
              data-testid="geofence-unavailable"
            >
              {translate('Geofence controls are not available on this card.')}
            </p>
          ) : (
            <>
              {/*
                A non-AVAILABLE snapshot still has a reason worth showing. An
                UNAVAILABLE one has no country list either, so the pills and the
                drawer trigger are withheld rather than rendered empty.
              */}
              {geofence !== undefined &&
                geofence.availability !== 'AVAILABLE' && (
                  <p
                    className="SpendRestrictionsTile-body"
                    data-testid="geofence-unavailable"
                  >
                    {geofenceUnavailableText(translate, geofence)}
                  </p>
                )}
              {permittedCountries !== null && (
                <>
                  {/*
                    No duplicate country-pills block here: `SettingsDisclosure`
                    keeps its `summary` (`geofenceSummary`) visible even while
                    expanded, so a second copy in the body repeated the same
                    pills. MCC does not have this bug — its body renders
                    category rows, not its own summary — which made Geofence
                    the odd one out.
                  */}
                  <Button
                    variant="secondary"
                    dropShadow={false}
                    onClick={() => setGeofenceDrawerOpen(true)}
                    data-testid="edit-geofence"
                  >
                    {geofenceIsEditable
                      ? translate('Edit countries')
                      : translate('View countries')}
                  </Button>
                </>
              )}
            </>
          )}
        </SettingsDisclosure>
      </div>

      {permittedCountries !== null && (
        <GeofenceDrawer
          open={geofenceDrawerOpen}
          onClose={() => setGeofenceDrawerOpen(false)}
          permittedCountries={permittedCountries}
          canEdit={geofenceIsEditable}
          query={geofenceCountryQuery}
          onQueryChange={onGeofenceCountryQueryChange ?? NOOP}
          results={geofenceCountryResults ?? []}
          searchState={geofenceCountrySearchState}
          onAdd={(countryCode) => changeGeofenceCountry('ADD', countryCode)}
          onRemove={(countryCode) =>
            changeGeofenceCountry('REMOVE', countryCode)
          }
          pending={pendingGeofenceCountry}
          saveError={geofenceSaveError}
        />
      )}
    </SettingsTile>
  )
}
